'use client';

import React, { useState, useMemo, useEffect } from 'react';
import { fetchExams, Exam } from '@/lib/services/examService';
import ExamCard from './ExamCard';
import ExamDetailPanel from './ExamDetailPanel';
import { Search, X, Filter, Loader2 } from 'lucide-react';

const streamFilters = ['All', 'Engineering', 'Medical', 'Management', 'Law', 'Arts', 'Commerce'];
const levelFilters = ['All', 'National', 'State', 'University'];
const modeFilters = ['All', 'Online', 'Offline', 'Both'];

export default function ExamPageContent() {
  const [exams, setExams] = useState<Exam[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedExam, setSelectedExam] = useState<Exam | null>(null);
  const [streamFilter, setStreamFilter] = useState('All');
  const [levelFilter, setLevelFilter] = useState('All');
  const [modeFilter, setModeFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchExams().then((data) => {
      setExams(data);
      if (data.length > 0) setSelectedExam(data[0]);
      setLoading(false);
    });
  }, []);

  const filteredExams = useMemo(() => {
    return exams.filter((exam) => {
      const matchStream = streamFilter === 'All' || exam.stream.includes(streamFilter as any);
      const matchLevel = levelFilter === 'All' || exam.level === levelFilter;
      const matchMode = modeFilter === 'All' || exam.mode === modeFilter;
      const matchSearch =
        searchQuery === '' ||
        exam.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        exam.shortName.toLowerCase().includes(searchQuery.toLowerCase());
      return matchStream && matchLevel && matchMode && matchSearch;
    });
  }, [exams, streamFilter, levelFilter, modeFilter, searchQuery]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 size={32} className="animate-spin text-primary" />
        <span className="ml-3 text-muted-foreground">Loading exams...</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      {/* Left: Exam list */}
      <div className="w-full lg:w-96 xl:w-[420px] shrink-0">
        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
          <input
            type="text"
            placeholder="Search exams..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input-base pl-9"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X size={15} />
            </button>
          )}
        </div>

        {/* Filter pills */}
        <div className="space-y-2 mb-4">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-xs font-semibold text-muted-foreground w-14 shrink-0">Stream</span>
            {streamFilters.map((f) => (
              <button
                key={`stream-filter-${f}`}
                onClick={() => setStreamFilter(f)}
                className={`text-xs px-2.5 py-1 rounded-full font-semibold border transition-all ${
                  streamFilter === f
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'border-border text-muted-foreground hover:border-primary hover:text-primary'
                }`}
              >
                {f}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-xs font-semibold text-muted-foreground w-14 shrink-0">Level</span>
            {levelFilters.map((f) => (
              <button
                key={`level-filter-${f}`}
                onClick={() => setLevelFilter(f)}
                className={`text-xs px-2.5 py-1 rounded-full font-semibold border transition-all ${
                  levelFilter === f
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'border-border text-muted-foreground hover:border-primary hover:text-primary'
                }`}
              >
                {f}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-xs font-semibold text-muted-foreground w-14 shrink-0">Mode</span>
            {modeFilters.map((f) => (
              <button
                key={`mode-filter-${f}`}
                onClick={() => setModeFilter(f)}
                className={`text-xs px-2.5 py-1 rounded-full font-semibold border transition-all ${
                  modeFilter === f
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'border-border text-muted-foreground hover:border-primary hover:text-primary'
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        {/* Result count */}
        <p className="text-xs text-muted-foreground mb-3 font-tabular">
          Showing <span className="font-semibold text-foreground">{filteredExams.length}</span> exams
        </p>

        {/* Exam cards list */}
        <div className="space-y-3 overflow-y-auto scrollbar-thin" style={{ maxHeight: 'calc(100vh - 18rem)' }}>
          {filteredExams.length === 0 ? (
            <div className="card-base p-8 text-center">
              <Filter size={32} className="text-muted-foreground mx-auto mb-3" />
              <p className="font-semibold text-foreground mb-1">No exams match your filters</p>
              <p className="text-sm text-muted-foreground">Try adjusting the stream, level, or mode filters above.</p>
              <button
                onClick={() => { setStreamFilter('All'); setLevelFilter('All'); setModeFilter('All'); setSearchQuery(''); }}
                className="btn-secondary mt-4 text-sm py-1.5 px-4"
              >
                Reset Filters
              </button>
            </div>
          ) : (
            filteredExams.map((exam) => (
              <ExamCard
                key={`exam-card-${exam.id}`}
                exam={exam}
                isSelected={selectedExam?.id === exam.id}
                onSelect={() => setSelectedExam(exam)}
              />
            ))
          )}
        </div>
      </div>

      {/* Right: Detail panel */}
      <div className="flex-1 min-w-0">
        {selectedExam ? (
          <ExamDetailPanel exam={selectedExam} />
        ) : (
          <div className="card-base p-12 text-center h-full flex flex-col items-center justify-center">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4 mx-auto">
              <Search size={28} className="text-muted-foreground" />
            </div>
            <p className="font-semibold text-foreground mb-1">Select an exam to view details</p>
            <p className="text-sm text-muted-foreground">Click any exam card on the left to see full details, cutoff scores, and important dates.</p>
          </div>
        )}
      </div>
    </div>
  );
}